/**
 *
 *
 * @author Auto
 * @date 2024
 */
import PostForm from "./PostForm";
import Utils from "../utils/Utils";

const RECAPTCHA_SITE_KEY = process.env.RECAPTCHA_SITE_KEY || "NO_RECAPTCHA_SITE_KEY";

export default class RecaptchaForm extends PostForm {
    constructor(panelElement, recaptchaAction) {
        super();
        this.formElement = panelElement;
        this.formElement && this.bindForm();
        this.recaptchaAction = recaptchaAction;
        this._consentListenerAdded = false;
    }

    init() {
        this.loadRecaptchaScript();
        this.updateBadgeMessage();
        this.updateSubmitButtonState();
    }

    isCookieConsentEnabled() {
        const cookieConsent = window.interface_settings?.settings?.cookieConsent;
        return cookieConsent && cookieConsent !== false && cookieConsent.mode >= 1;
    }

    submitForm(e) {
        e.preventDefault();
        let fields = Array.from(this.formElement.querySelectorAll("[data-validator]")),
            validatedFields = fields.map((entry) => {
                return this.validateField(entry);
            });
        // every validatedField must be valid. the entry is automatically returned in the arrow function without braces
        if (validatedFields.every((entry) => entry)) {
            this.submitFormWithRecaptcha();
        }
    }

    loadRecaptchaScript() {
        console.log("MAGLR DEBUG: [RecaptchaForm.loadRecaptchaScript] Loading reCAPTCHA script");
        if (document.querySelector('script[src*="recaptcha/enterprise.js"]')) {
            return Promise.resolve();
        }

        if (this.isCookieConsentEnabled() && window.MaglrCookieConsented !== true) {
            if (window.frontend?.CookieConsent) {
                window.frontend.CookieConsent.showAgain();
            }
            this._setupConsentListener();
            return Promise.reject(new Error("Cookie consent required to load reCAPTCHA"));
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Failed to load reCAPTCHA script"));
            document.head.appendChild(script);
        });
    }

    submitFormWithRecaptcha() {
        if (RECAPTCHA_SITE_KEY === "NO_RECAPTCHA_SITE_KEY") {
            console.error(
                "MAGLR ERROR: [RecaptchaForm.submitFormWithRecaptcha] RECAPTCHA_SITE_KEY is not set. Please set RECAPTCHA_SITE_KEY in your .env file.",
            );
            this.postError({ status: 500, statusText: "reCAPTCHA configuration error" });
            return;
        }

        this.loadRecaptchaScript()
            .then(() => {
                // eslint-disable-next-line no-undef
                if (typeof grecaptcha === "undefined" || !grecaptcha.enterprise) {
                    console.error(
                        "MAGLR ERROR: [RecaptchaForm.submitFormWithRecaptcha] reCAPTCHA Enterprise not loaded",
                    );
                    this.postError({ status: 500, statusText: "reCAPTCHA not available" });
                    return;
                }

                // eslint-disable-next-line no-undef
                grecaptcha.enterprise.ready(() => {
                    // eslint-disable-next-line no-undef
                    grecaptcha.enterprise
                        .execute(RECAPTCHA_SITE_KEY, { action: this.recaptchaAction })
                        .then((token) => {
                            this.submitFormData(token);
                        })
                        .catch((error) => {
                            console.error("MAGLR ERROR: [RecaptchaForm.submitFormWithRecaptcha]", error);
                            this.postError({ status: 500, statusText: "reCAPTCHA execution failed" });
                        });
                });
            })
            .catch((error) => {
                console.error("MAGLR ERROR: [RecaptchaForm.loadRecaptchaScript]", error);
                this.postError({ status: 500, statusText: "Failed to load reCAPTCHA" });
            });
    }

    buildBaseFormData(recaptchaToken) {
        let fields = Array.from(this.formElement.querySelectorAll("[data-validator]"));
        let formData = new FormData();
        formData.append("domain", window.top_hostname || location.hostname);
        window.frontend && formData.append("lang", window.frontend.getLanguage());
        fields.forEach((field) => {
            formData.append(field.dataset["name"], this.getFieldValue(field));
        });
        formData.append("recaptcha_token", recaptchaToken);
        return formData;
    }

    getDashDomain() {
        let dash_domain = window.dash_domain;
        if (Utils.isEmpty(dash_domain)) {
            dash_domain = "https://dashboard.maglr.io";
        }
        return dash_domain;
    }

    submitToApi(formData, apiEndpoint) {
        return this.postForm(formData, this.getDashDomain() + apiEndpoint)
            .then((response) => {
                this.postSuccess(response);
            })
            .catch((response) => {
                this.postError(response);
            });
    }

    updateBadgeMessage() {
        if (!this.formElement) {
            return;
        }

        const warningElement = this.formElement.querySelector(".recaptcha-cookie-warning");
        const badgeElement = this.formElement.querySelector(".recaptcha-badge");

        if (warningElement && badgeElement) {
            if (!this.isCookieConsentEnabled()) {
                warningElement.style.display = "none";
                badgeElement.style.display = "";
            } else if (window.MaglrCookieConsented === true) {
                warningElement.style.display = "none";
                badgeElement.style.display = "";
            } else {
                warningElement.style.display = "";
                badgeElement.style.display = "none";
            }
        }
    }

    updateSubmitButtonState() {
        if (!this.formElement) {
            return;
        }

        const submitButton = this.formElement.querySelector('input[type="submit"]');
        if (submitButton) {
            if (!this.isCookieConsentEnabled() || window.MaglrCookieConsented === true) {
                submitButton.removeAttribute("disabled");
            } else {
                submitButton.setAttribute("disabled", "disabled");
            }
        }
    }

    _setupConsentListener() {
        if (this._consentListenerAdded || !window.frontend?.CookieConsent) {
            return;
        }

        this._consentListenerAdded = true;
        const consentHandler = (event) => {
            this.updateBadgeMessage();
            this.updateSubmitButtonState();

            if (event.consent === true) {
                this.loadRecaptchaScript().catch((error) => {
                    console.error(
                        "MAGLR ERROR: [RecaptchaForm._setupConsentListener] Failed to load reCAPTCHA after consent",
                        error,
                    );
                });
            }
        };

        window.frontend.CookieConsent.on("ConsentChanged", consentHandler);
        this._consentHandler = consentHandler;
    }

    // eslint-disable-next-line no-unused-vars
    submitFormData(recaptchaToken) {
        throw new Error("submitFormData must be implemented by child class");
    }
}
